import each from 'lodash/each'
import isObject from 'lodash/isObject'
import memoize from 'lodash/memoize'
import { EventTracker, Events } from '@everlane/event-tracking'
import { Builder } from '@builder.io/react'

import Hub from 'base/hub'
import Event from 'base/event'
import store from 'base/store'

// Rules for how properties get transformed for consumption by analytics.js
const propertyFilter = {
  'Product.PAGE_VIEW'(eventData) {
    return {
      product_id: eventData.product.id,
      permalink: eventData.product.permalink,
      collection: eventData.collection && eventData.collection.permalink,
      product_name: eventData.product.display_name,
      product_price: eventData.product.price,
    }
  },

  'Product.SIZE_CHANGE'(eventData) {
    return {
      product_id: eventData.id,
      permalink: eventData.permalink,
      size: eventData.size,
      collection: eventData.collection && eventData.collection.permalink,
    }
  },

  'Product.COLOR_CHANGE'(eventData) {
    return {
      product_id: eventData.id,
      permalink: eventData.permalink,
      color: eventData.color.name,
      collection: eventData.collection && eventData.collection.permalink,
    }
  },

  'Collections.PAGE_VIEW'(eventData) {
    return { permalink: eventData.permalink }
  },

  'Checkout.ADD_TO_CART'(eventData) {
    return {
      variant_id: eventData.variant.id,
      location: eventData.location,
      bundle_item: eventData.bundle_item,
      collection: eventData.collection && eventData.collection.permalink,
      product_name: eventData.variant.display_name,
      product_price: eventData.variant.price,
      product_id: eventData.variant.product_id,
    }
  },
}

// Publish event to @everlane/event-tracking/src/events
function publishEvent(event, data) {
  if (propertyFilter[event]) {
    const dataArguments = [...arguments].slice(1, arguments.length)

    data = propertyFilter[event].apply(null, dataArguments)
  }

  event = JSON.parse(JSON.stringify(event))
  if (data) {
    data = JSON.parse(JSON.stringify(data))
  } // Sometimes `data` is undefined

  eventTracker().capture(event, data)
}

// Initialize all auto-trackers
export default function setupAutotrack() {
  const isEditingBuilder = !!Builder.previewingModel

  if (isEditingBuilder) return

  each(Event, eventOrEvents => {
    const recursiveSubscribe = eventOrEvents => {
      if (isObject(eventOrEvents) && !eventOrEvents.name) {
        return each(eventOrEvents, recursiveSubscribe)
      } else if (eventOrEvents.autoTrack !== false) {
        // NEW SUBSCRIBE
        // We added this to subscribe to events which are published
        // in Builder using Events.publish events from
        // builder TrackEvents.
        Events.subscribe(eventOrEvents.name, payload => {
          eventTracker().capture(eventOrEvents.name, payload)
        })

        // TODO: Replace legacy Hub pub/sub with Events.
        // LEGACY SUBSCRIBE
        return Hub.sub(eventOrEvents, publishEvent)
      }
    }

    return recursiveSubscribe(eventOrEvents)
  })
}

export const eventTracker = memoize(() => {
  const { eventTrack } = store.getState().appConfig

  return new EventTracker({
    apiUrl: eventTrack.apiUrl,
    apiKey: eventTrack.apiKey,
    properties: getProperties,
  })
})

function getProperties() {
  const { user, visitor } = store.getState().session
  const { isMobile } = store.getState().appConfig

  return {
    user_id: user?.id,
    session_id: visitor.session_id,
    platform: isMobile ? 'mobile' : 'desktop',
  }
}
